import React, { useEffect, useState, useRef } from "react";
import { app } from '../components/Firebase';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, onValue, update, get } from "firebase/database";
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import {ThemeProvider } from '@mui/material/styles';
import Copyright from '../components/Copyright';
import theme from '../components/theme';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { Hidden } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useNavigate } from "react-router-dom";
import { appCheck } from "../App";
import { getToken  } from "firebase/app-check";

const Product = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const auth = getAuth(app);
  const db = getDatabase(app);
  const hasRun = useRef(false);
  const [dialogueOpen, setDialogueOpen] = useState(false);
  const [dialogueCompanyNameOpen, setDialogueCompanyNameOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const primaryQuantities = [1, 25, 50, 100];

  const primaryProducts = Object.keys(products).filter(productId => primaryQuantities.includes(products[productId].quantity));
  const secondaryProducts = Object.keys(products).filter(productId => !primaryQuantities.includes(products[productId].quantity));

  const handleCompanyNameSubmit = async () => {
    if (companyName && auth.currentUser) {
      setDialogueCompanyNameOpen(false);
      // User is signed in.
      try {
        const userRef = ref(db, 'User/' + auth.currentUser.uid);
        // Update the user's company name in the database.
        await update(userRef, {
          companyName: companyName,
        });
        console.log("Company name updated or added");
      } catch (error) {
        console.error("Error updating company name: ", error);
        alert("Error updating company name.");
      }
    }
  };

  useEffect(() => {
    const fetchAndPromptCompanyName = async (uid) => {
      if (uid) {
        // User is signed in.
        const companyNameRef = ref(db, 'User/' + uid+'/companyName');
        let companyNameData = null;
        get(companyNameRef).then((snapshot) => {
          companyNameData = snapshot.val();
          if (companyNameData) {
            console.log("has company name");
          } else {
            // Prompt the user to enter their company name.
            setDialogueCompanyNameOpen(true);
            console.log("prompting for company name");
          }
        });
      }
    };

    const unsubscribe = onAuthStateChanged(auth, () => {
      const productRef = ref(db, 'Product');
      onValue(productRef, (snapshot) => {
        const data = snapshot.val();
        setProducts(data);
      });
      fetchAndPromptCompanyName(auth.currentUser?.uid);
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);  

  //create checkout session
  const handleProductClick = async (productId) => {
    if (!auth?.currentUser)
    {
      navigate("/signup");
      return;
    }
    setLoading(true);
    let retryCount = 0;
    let success = false;
    let error = null;

    while (retryCount < 3 && !success) {
      try {
        // Get the ID token of the current user.
        const idToken = await auth.currentUser.getIdToken();
        // Get a valid App Check token
        let appCheckTokenResponse;
        try {
          appCheckTokenResponse = await getToken(appCheck, false);
        } catch (err) {
          console.error("App Check token retrieval failed:", err);
          return; // Abort if unable to retrieve token
        }
        // Create a checkout session for the clicked product
        const response = await fetch(`/createCheckoutSession`, 
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'X-Firebase-AppCheck': appCheckTokenResponse.token
          },
          body: JSON.stringify({
            productId: productId
          })
        });

        const responseText = await response.text();

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Then parse it as JSON
        const { sessionId } = JSON.parse(responseText);

        // Redirect to the Stripe Checkout, uses public stripe key
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
        const { error } = await stripe.redirectToCheckout({ sessionId });

        if (error) {
          console.warn('Error:', error);
          throw error;
        }

        success = true;
      } catch (err) {
        retryCount++;
        error = err;
        console.warn(`Attempt ${retryCount} failed. Retrying...`);
      }
    }

    if (!success) {
      console.warn('All attempts failed:', error);
      // Open the dialog
      setDialogueOpen(true);
    }
    setLoading(false);
  };



  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main>
        <Box
          sx={{
            bgcolor: 'background.paper',
            pt: 8,
            pb: 6,
          }}
        >
          <Container maxWidth="sm">
          <Hidden xsUp={!loading}>
            <CircularProgress color="success" m={10}/>
          </Hidden>
            <Typography
              component="h1"
              variant="h4"
              align="center"
              color="text.primary"
              gutterBottom
            >
              Assessment Packages
            </Typography>
            <Typography variant="h6" align="left" color="text.secondary" paragraph>
              Once you purchase a package, you can use the purchased assessments in any quantity, at any time, through the Request Assessments page.
            </Typography>
            <Dialog
              open={dialogueOpen}
              onClose={() => setDialogueOpen(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">{"Checkout Session Failed"}</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  All attempts to create a checkout session failed. Please try again later.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogueOpen(false)} color="primary" autoFocus>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog open={dialogueCompanyNameOpen} onClose={() => {}}>
              <DialogTitle>Enter Company Name</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Please enter your company name.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  id="companyName"
                  label="Company Name"
                  type="text"
                  fullWidth
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  inputProps={{
                    maxLength: 100,
                  }}
                  error={companyName && companyName.length > 100}
                  helperText={(companyName && companyName.length > 100) ? "Company Name must be 100 characters or less" : ""}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCompanyNameSubmit} color="primary">
                  Submit
                </Button>
              </DialogActions>
            </Dialog>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1em' }}>
              {primaryProducts.map(productId => {
                const product = products[productId];
                return (
                  <Paper key={productId} elevation={3} style={{ padding: '1em', borderRadius: '15px' }}>
                    <h2>{product.name}</h2>
                    <p>{product.quantity}x Assessments</p>
                    <p>Unit Price: ${product.price / 100}</p>
                    <p>Total price: ${(product.price * product.quantity) / 100}</p>
                    <Button variant="contained" color="primary" onClick={() => handleProductClick(productId)}>
                      Buy
                    </Button>
                  </Paper>
                );
              })}
            </div>
            <Button variant="contained" color="primary" onClick={() => setShowMore(!showMore)} style={{ marginTop: '1em' }}>
              {showMore ? 'Less Package Options' : 'More Package Options'}
            </Button>
            {showMore && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1em' }}>
                {secondaryProducts.map(productId => {
                  const product = products[productId];
                  return (
                    <Paper key={productId} elevation={3} style={{ padding: '1em', borderRadius: '15px' }}>
                      <h2>{product.name}</h2>
                      <p>{product.quantity}x Assessments</p>
                      <p>Unit Price: ${product.price / 100}</p>
                      <p>Total price: ${(product.price * product.quantity) / 100}</p>
                      <Button variant="contained" color="primary" onClick={() => handleProductClick(productId)}>
                        Buy
                      </Button>
                    </Paper>
                  );
                })}
              </div>
            )}
          </Container>
        </Box>
      </main>
      <Copyright sx={{ mt: 5 }} />
    </ThemeProvider>
  );
}
export default Product;
